import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { GestureDetector, RefreshControl } from 'react-native-gesture-handler';
import Animated, { interpolate, useAnimatedStyle } from 'react-native-reanimated';

import { createMaterialTopTabNavigator } from '@react-navigation/material-top-tabs';
import { EventArg, RouteProp, TabNavigationState, useNavigation, useRoute } from '@react-navigation/native';

import { Screen } from '@/components/ScreenComponent';
import { ScreenNavBar } from '@/components/ScreenNavBar';
import { Section } from '@/components/Section';
import { SizedBox } from '@/components/SizedBox';
import { StickyTabsProvider, useStickyTabList, useStickyTabsAnimation } from '@/components/StickyTabsProvider';
import { Text } from '@/components/TextComponent';
import { PROMOS_TOP_TAB_BAR_CONFIG, TopTabBar, TopTabBarProps } from '@/components/TopTabBar';
import { Box } from '@/components/lib/components';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { GameResponsibly } from '@/feature/invite-friends/components/GameResponsibly';
import { Promotion, usePromotions } from '@/feature/promohub/hooks/use-promotions';
import { useResponsiveColumnCount } from '@/feature/responsive-design/hooks/use-responsive-column-count';
import { useResumeEffect } from '@/hooks/use-resume';
import { PromohubStackParamList } from '@/navigation/types';
import { common, designSystem } from '@/styles/styles';
import { isWeb, showScrollIndicator } from '@/utils/constants-platform-specific';

import { PromoCard } from '../components/PromoCard';

type PromohubTabParamList = { Available: undefined; 'Opted-in': undefined };

const Tab = createMaterialTopTabNavigator<PromohubTabParamList>();

const TITLE_HEIGHT = 40;
const PROMOS_NAV_BAR_HEIGHT = 48;

const styles = StyleSheet.create({
    card: {
        borderColor: designSystem.colors.gray5,
        borderWidth: 1,
        borderRadius: 12,
        backgroundColor: designSystem.colors.gray6,
        justifyContent: 'space-between',
        flex: 1,
    },
    columns: { gap: 15 },
    headerWeb: {
        height: PROMOS_NAV_BAR_HEIGHT,
        justifyContent: 'center',
    },
    topTabBarContainer: {
        backgroundColor: designSystem.colors.gray8,
        top: TITLE_HEIGHT,
    },
    tabSeparator: { width: 0 },
    headerContainer: { zIndex: 2 },
    screenNavBar: { position: 'absolute', top: 0, left: 0, right: 0 },
    contentContainer: {
        paddingTop: TITLE_HEIGHT + PROMOS_NAV_BAR_HEIGHT,
        paddingBottom: 16,
        paddingHorizontal: 16,
    },
    tabContainer: {
        paddingTop: 24,
    },
    gameResponsibly: {
        paddingTop: 80,
    },
    screenNavBarContainer: {
        height: PROMOS_NAV_BAR_HEIGHT,
        marginTop: 8,
    },
    titleContainer: {
        backgroundColor: designSystem.colors.gray8,
        paddingTop: 4,
        top: PROMOS_NAV_BAR_HEIGHT,
    },
});

const ItemSeparator = () => <SizedBox value={16} />;

export const PromohubTopTabBarScreen = () => (
    <StickyTabsProvider headerHeight={TITLE_HEIGHT}>
        <PromohubScreen />
    </StickyTabsProvider>
);

export type PromohubParams = RouteProp<PromohubStackParamList, 'Promohub'>;

export const PromohubScreen = () => {
    const { params } = useRoute<PromohubParams>();
    const navigation = useNavigation();

    const { promotions, refetch, isLoading, showRgFooter, goToPromo: handleGoToPromo } = usePromotions();
    const { panGesture, smoothScrollHeaderStyleBelowTabsPullToRefresh, tabListeners, scrollY, headerHeight } =
        useStickyTabsAnimation();
    const [locallyOptedInPromos, setLocallyOptedInPromos] = useState<Set<string>>(new Set());

    // Locally store opted-in promo IDs to keep them visible (with opted-in status)
    // in the "Available" tab until the user switches tabs, click on Promo Details or leaves the screen.
    const handleLocalOptIn = (promoId: string) => {
        setLocallyOptedInPromos(prev => new Set([...prev, promoId]));
    };

    const goToPromo = useCallback(
        (promoId: string, external_url?: string | null) => {
            handleGoToPromo(promoId, external_url);
            setLocallyOptedInPromos(new Set());
        },
        [handleGoToPromo, setLocallyOptedInPromos]
    );

    const promoTabsListeners = useMemo(() => {
        return {
            ...tabListeners,
            state: (e: EventArg<'state', undefined, { state: TabNavigationState<any> }>) => {
                tabListeners?.state?.(e);
                // refetch promotions when the user navigates to a different tab
                setLocallyOptedInPromos(new Set());
                refetch();
            },
        };
    }, [tabListeners, setLocallyOptedInPromos, refetch]);

    const reload = useCallback(() => {
        setLocallyOptedInPromos(new Set());
        refetch();
    }, [refetch]);

    const { showToast } = useAlerts();
    const columnCount = useResponsiveColumnCount([1, 1, 1, 2, 3]);

    const { t } = useTranslation(['promohub']);

    useEffect(() => {
        if (params.promoId && !isLoading) {
            const promoData = promotions.find(promo => promo.uid === params.promoId);
            if (promoData) {
                goToPromo(params.promoId, promoData.data.external_url);
            } else {
                showToast({ message: t('promotion_not_valid'), toastType: 'warning' });
            }
            // clear the params so the promotion doesn't open up again if the user navigates back to the promohub
            navigation.setParams({ promoId: undefined });
        }
    }, [goToPromo, isLoading, navigation, params.promoId, promotions, showToast, t]);

    useResumeEffect(reload);

    const renderStickyTabBar = useCallback(
        (props: TopTabBarProps) => {
            return (
                <Animated.View style={[smoothScrollHeaderStyleBelowTabsPullToRefresh, styles.topTabBarContainer]}>
                    <TopTabBar {...props} customEdges={[]} separatorStyles={styles.tabSeparator} />
                </Animated.View>
            );
        },
        [smoothScrollHeaderStyleBelowTabsPullToRefresh]
    );

    const translationDistance = headerHeight + PROMOS_NAV_BAR_HEIGHT;

    const optedInPromotions = promotions.filter(promo => promo.optedIn);
    const availablePromotions = promotions.filter(promo => !promo.optedIn || locallyOptedInPromos.has(promo.uid));

    const navBarAnimatedStyle = useAnimatedStyle(() => {
        const opacity = interpolate(scrollY.value, [20, 40], [0, 1], 'clamp');
        return {
            opacity,
        };
    });
    return (
        <Screen edges={['top']}>
            <View style={[common.noOverflow, common.flex]}>
                <Section style={[common.noPadding, isWeb && styles.headerWeb, styles.headerContainer]}>
                    <ScreenNavBar
                        title={''}
                        closeIconMode={params.mode === 'stack' ? 'back' : 'none'}
                        style={styles.screenNavBarContainer}
                    />
                    <Animated.View style={[navBarAnimatedStyle, styles.screenNavBar]}>
                        <ScreenNavBar
                            title={t('promohub:promos')}
                            closeIconMode={params.mode === 'stack' ? 'back' : 'none'}
                            style={styles.screenNavBarContainer}
                        />
                    </Animated.View>
                </Section>
                <GestureDetector gesture={panGesture}>
                    <Animated.View
                        style={[common.full, smoothScrollHeaderStyleBelowTabsPullToRefresh, styles.titleContainer]}
                    >
                        <Text
                            variant={'displayMedium'}
                            color={'white'}
                            fontWeight={'bold'}
                            fontSize={32}
                            lineHeight={40}
                            paddingHorizontal={'s16'}
                            marginBottom={'s4'}
                        >
                            {t('promohub:promos')}
                        </Text>
                    </Animated.View>
                </GestureDetector>

                <Box flex={1}>
                    <Tab.Navigator
                        screenListeners={promoTabsListeners}
                        tabBar={renderStickyTabBar}
                        initialRouteName="Available"
                        screenOptions={PROMOS_TOP_TAB_BAR_CONFIG}
                        style={styles.tabContainer}
                    >
                        <Tab.Screen name={'Available'} key={'Available'} options={{ title: t('promohub:available') }}>
                            {() => (
                                <PromotionsList
                                    promotions={availablePromotions}
                                    handleLocalOptIn={handleLocalOptIn}
                                    isLoading={isLoading}
                                    reload={reload}
                                    goToPromo={goToPromo}
                                    columnCount={columnCount}
                                    showRgFooter={showRgFooter}
                                    translationDistance={translationDistance}
                                    type={'available'}
                                />
                            )}
                        </Tab.Screen>
                        <Tab.Screen name={'Opted-in'} key={'Opted-in'} options={{ title: t('promohub:opted_in') }}>
                            {() => (
                                <PromotionsList
                                    promotions={optedInPromotions}
                                    handleLocalOptIn={handleLocalOptIn}
                                    isLoading={isLoading}
                                    reload={reload}
                                    goToPromo={goToPromo}
                                    columnCount={columnCount}
                                    showRgFooter={showRgFooter}
                                    translationDistance={translationDistance}
                                    type={'opted-in'}
                                />
                            )}
                        </Tab.Screen>
                    </Tab.Navigator>
                </Box>
            </View>
        </Screen>
    );
};

type PromotionsListTypes = {
    promotions: Promotion[];
    handleLocalOptIn: (promoId: string) => void;
    isLoading: boolean;
    reload: () => void;
    goToPromo: (promoId: string, external_url?: string | null) => void;
    columnCount: number;
    showRgFooter?: boolean;
    translationDistance: number;
    type: 'available' | 'opted-in';
};

export const PromotionsList = ({
    promotions,
    handleLocalOptIn,
    isLoading,
    reload,
    goToPromo,
    columnCount,
    showRgFooter,
    translationDistance,
    type,
}: PromotionsListTypes) => {
    const { scrollableProps, setStickyRef, contentHeight } = useStickyTabList(type, false, reload, true);

    return (
        <Animated.FlatList
            ref={setStickyRef}
            {...scrollableProps}
            keyExtractor={item => item.uid}
            key={`${columnCount}`}
            scrollEventThrottle={16}
            data={promotions}
            refreshControl={
                <RefreshControl
                    enabled
                    colors={[designSystem.colors.white]}
                    tintColor={designSystem.colors.white}
                    refreshing={isLoading}
                    progressViewOffset={translationDistance}
                    onRefresh={() => reload()}
                />
            }
            numColumns={columnCount}
            style={{ backgroundColor: designSystem.colors.gray8 }}
            columnWrapperStyle={columnCount !== 1 ? styles.columns : undefined}
            contentContainerStyle={[styles.contentContainer, { minHeight: contentHeight + translationDistance }]}
            ItemSeparatorComponent={ItemSeparator}
            showsVerticalScrollIndicator={showScrollIndicator}
            renderItem={({ item }) => (
                <PromoCard
                    key={item.uid}
                    promoId={item.uid}
                    segmented={item.data.segmented}
                    showOptInButton={item.bonusEngineDetails?.optInRequired ?? item.data.display_cta}
                    redirectPath={item.data.redirect_path ?? undefined}
                    title={item.data.title ?? ''}
                    imageUri={item.data.promocard_image_square.url ?? ''}
                    expirationTime={item.bonusEngineDetails?.expirationTime}
                    hasOptedIn={item.optedIn}
                    onPress={() => goToPromo(item.uid, item.data.external_url)}
                    dynamicHeight={true}
                    theme={item.data.promo_theme.data}
                    secondaryButton={item.data.secondary_cta ?? undefined}
                    onLocalOptIn={handleLocalOptIn}
                />
            )}
            ListFooterComponent={showRgFooter ? <GameResponsibly style={styles.gameResponsibly} /> : null}
        />
    );
};
